import { html, css, LitElement } from 'lit-element';
import '@trumedianetworks/tmn-core-components/src/tmn-radio';
import '@trumedianetworks/tmn-core-components/src/tmn-input';
import '@trumedianetworks/tmn-core-components/src/tmn-checkbox';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import '@trumedianetworks/tmn-core-components/src/tmn-select';

// default to only selected and select the first two, see if I know "active season" from dp
const LIMIT_TYPE = {
    NONE: 'NONE', // meaning all available
    ALLOW: 'ALLOW',
    DENY: 'DENY',
};

const limitOptions = [
    {
        name: LIMIT_TYPE.NONE,
        displayKey: 'All Seasons',
    },
    {
        name: LIMIT_TYPE.ALLOW,
        displayKey: 'Allow Selected Seasons',
    },
    {
        name: LIMIT_TYPE.DENY,
        displayKey: 'Allow All Except Selected Seasons',
    },
];
// base class since stats-perform league detail (cricket and soccer are very similar)
export class SPLeagueDetail extends LitElement {
    constructor() {
        super();
        this.seasons = [];
        this.limitType = LIMIT_TYPE.NONE;
    }

    static get styles() {
        return [
            // HeaderStyles.styles,
            css`
                :host {
                    padding: 0 10px;
                    border-radius: 8px;
                    background-color: #f9f9f9;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                }
                tmn-select {
                    min-width: 182px;
                }
                tmn-button {
                    margin: 8px;
                }
                tmn-checkbox {
                    width: fit-content;
                }
                .unchecked {
                    color: rgba(0, 0, 0, 0.1);
                }
                .detail-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 4px 0;
                }
                .detail-header > div {
                    display: flex;
                }
                .league-name {
                    font-weight: 600;
                    font-size: 1rem;
                }
                .limit-note {
                    display: flex;
                    justify-content: flex-start;
                    font-weight: 600;
                    font-size: 1rem;
                    color: var(--app-secondary-color);
                }
                tmn-icon-button {
                    margin-left: 6px;
                }
                .season-display {
                    border: 1px solid var(--app-secondary-color);
                    margin: 4px;
                    padding: 4px;
                    border-radius: 4px;
                    display: inline-flex;
                }
                .content {
                    padding-top: 8px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            seasons: {
                type: Array,
            },
            availableSeasons: {
                type: Array,
            },
            isViewMode: {
                type: Boolean,
            },
            displayKey: {
                type: String,
            },
            leagueId: {
                type: String,
            },
            limitType: {
                type: String,
            },
        };
    }

    render() {
        if (this.isViewMode) {
            return html`<div class="detail-header">${this.renderDisplayHeader()}</div>
                <div class="content">${this.renderDisplaySelectedSeasons()}</div>`;
        }
        return html`
            <div class="detail-header">${this.renderEditHeader()}</div>
            <div class="content">${this.renderEditSelectedSeasons()}</div>
        `;
    }

    renderDisplayHeader() {
        const { displayKey } = this;
        return html`
            <div class="league-name">${displayKey}</div>
            <div></div>
        `;
    }

    // consider selector
    getAllowedSeasons() {
        const { seasons = [], availableSeasons = [], limitType } = this;
        if (limitType === LIMIT_TYPE.NONE) {
            return availableSeasons;
        }
        if (limitType === LIMIT_TYPE.DENY) {
            return availableSeasons.filter((as) => !seasons.includes(as.name));
        }
        return availableSeasons.filter((as) => seasons.includes(as.name));
    }

    renderDisplaySelectedSeasons() {
        const { seasons = [], availableSeasons, limitType } = this;
        if (limitType === LIMIT_TYPE.NONE) {
            return html`<div class="limit-note">All seasons allowed</div>`;
        }
        // this way keeps them in the same order as available
        const selectedSeasons = availableSeasons.filter((s) => seasons.includes(s.name));
        const seasonNames = selectedSeasons.map((s) => s.displayKey);

        if (limitType === LIMIT_TYPE.DENY) {
            return html`<div>All Except:</div>
                ${seasonNames.map((s) => html`<span class="season-display">${s}</span>`)}`;
        }
        return seasonNames.map((s) => html`<span class="season-display">${s}</span>`);
    }

    renderEditHeader() {
        const { displayKey } = this;
        return html`
            <div class="league-name">${displayKey}</div>
            <div>${this.renderLimitOptions()}</div>
        `;
    }

    renderEditSelectedSeasons() {
        const { seasons = [], availableSeasons, limitType } = this;
        if (limitType === LIMIT_TYPE.NONE) {
            return html`<div class="limit-note">All seasons allowed</div>`;
        }
        return availableSeasons.map(({ displayKey, name }) => {
            const seasonSelected = seasons.includes(name);
            return this.renderCheckbox(displayKey, name, seasonSelected);
        });
    }

    renderCheckbox(displayLabel, value, checked = false) {
        return html`<tmn-checkbox
            label=${displayLabel}
            .value=${value}
            .checked=${checked}
            @tmn-switch-change=${this.handleCheckboxChanged}
            >${displayLabel}
        </tmn-checkbox> `;
    }

    renderLimitOptions() {
        const { limitType } = this;
        const selection = limitOptions.filter((s) => s.name === limitType);
        return html`
            <tmn-select
                class="limit-select"
                .placeholder=${'Select Mode'}
                @tmn-selections-changed=${this.handleSelectChanged}
                .selections=${selection}
                .data=${limitOptions}
                nosearch></tmn-select>
            <tmn-icon-button title="remove" @click=${this.handleRemove}>
                <tmn-icon icon="remove_circle_outline"></tmn-icon>
            </tmn-icon-button>
        `;
    }

    handleSelectChanged(e) {
        const { leagueId, seasons = [] } = this;
        const { detail } = e;
        const { selections } = detail;
        const { name: seasonLimitType } = selections[0];
        const newOptions = {
            leagueId,
            seasons,
            seasonLimitType,
        };
        this.dispatchChanges(newOptions);
    }

    handleRemove(e) {
        const { leagueId } = this;
        const event = new CustomEvent('league-detail-removed', {
            detail: {
                leagueId,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    handleCheckboxChanged(e) {
        const { leagueId, seasons = [], limitType } = this;
        const { checked, value: seasonId } = e.detail;
        const newSeasons = checked ? [...seasons, seasonId] : seasons.filter((s) => s !== seasonId);
        const newOptions = {
            leagueId,
            seasonLimitType: limitType,
            seasons: newSeasons,
        };
        this.dispatchChanges(newOptions);
    }

    dispatchChanges(newOptions) {
        const event = new CustomEvent('league-detail-changed', {
            detail: {
                ...newOptions,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }
}
